import React, { type CSSProperties, type ReactElement, type ReactNode } from 'react'
import { useTheme } from '@mui/material/styles'
import Dialog, { type DialogProps } from '@mui/material/Dialog'
import { Box, Typography, CircularProgress } from '@mui/material'
import { BUTTON_SIZE, BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import ThemeButton from 'common/ui/theme-button'
import { StoreStatus } from 'common/utils/types'

import styles from './ThemeInputDialog.module.scss'

interface ThemeInputDialogProps extends DialogProps {
  open: boolean
  status?: StoreStatus
  title: string
  description?: string
  confirmButtonText?: string
  confirmButtonAction?: () => void
  confirmButtonDisabled?: boolean
  confirmButtonStyle?: CSSProperties
  declineButtonText?: string
  additionalButtonText?: string
  additionalButtonAction?: () => void
  onClose: () => void
  additionalComponent?: ReactNode
  additionalPreComponent?: ReactNode
  width?: string
  children: ReactNode
}

const ThemeInputDialog: React.FC<ThemeInputDialogProps> = ({
  open,
  title,
  description,
  confirmButtonText,
  confirmButtonAction,
  additionalButtonAction,
  confirmButtonDisabled = false,
  confirmButtonStyle,
  declineButtonText,
  additionalButtonText,
  onClose,
  additionalComponent,
  additionalPreComponent,
  width,
  children,
  status,
  ...rest
}): ReactElement => {
  const { palette } = useTheme()
  return (
    <Dialog
      PaperProps={{
        sx: {
          width,
          marginTop: 13,
        },
      }}
      open={open}
      onClose={onClose}
      {...rest}
    >
      <Box className={styles.container} width={width}>
        <Box className={styles.header}>
          <div className={styles.preComponent}>
            {additionalPreComponent}
            <div className={styles.titleRow}>
              <Typography variant="title1">{title}</Typography>
              {additionalComponent}
            </div>
          </div>
          {description && (
            <Typography variant="title5" sx={{ marginTop: '6px' }} color={palette.gray.main}>
              {description}
            </Typography>
          )}
        </Box>

        <Box className={styles.body}>{children}</Box>

        <div className={styles.buttonsContainer} style={confirmButtonStyle}>
          {additionalButtonText && (
            <ThemeButton
              size={BUTTON_SIZE.small}
              variant={BUTTON_VARIANTS.secondary}
              onClick={additionalButtonAction}
            >
              {additionalButtonText}
            </ThemeButton>
          )}
          {declineButtonText && (
            <ThemeButton size={BUTTON_SIZE.small} variant={BUTTON_VARIANTS.secondary} onClick={onClose}>
              {declineButtonText}
            </ThemeButton>
          )}
          {confirmButtonText && (
            <ThemeButton
              type="submit"
              size={BUTTON_SIZE.small}
              onClick={confirmButtonAction}
              disabled={confirmButtonDisabled}
            >
              {confirmButtonText}
              {status === StoreStatus.Loading && (
                <CircularProgress size={16} sx={{ marginLeft: '4px', color: palette.white.main }} />
              )}
            </ThemeButton>
          )}
        </div>
      </Box>
    </Dialog>
  )
}

export default ThemeInputDialog
